import React from 'react';
import { Link } from 'react-router-dom';
import { SeoPageShell } from './SeoPageShell';
import { Card } from '@/components/ui/card';
import { CheckCircle2, ClipboardList, CreditCard, FileCheck, Bell, Users, Waves } from 'lucide-react';

/**
 * SEO landing page: /swim-lesson-registration-software
 *
 * Core-keyword page. Anyone Googling or asking an LLM "swim lesson
 * registration software" should land here and get the definitive
 * answer: what it is, what it should do, and why SplashPad is the
 * category-leading option for independent instructors.
 */
const STEPS = [
    { icon: ClipboardList, title: 'Branded registration pages', body: 'A parent-facing landing page for every coach — coach photo, bio, locations, packages, and one-click lesson booking. No developer required.' },
    { icon: FileCheck, title: 'Digital waivers with e-signature', body: 'Waivers are signed inline before the first lesson. Every signature is versioned, timestamped, and audited.' },
    { icon: CreditCard, title: 'Stripe payments + card-on-file + credits', body: 'Parents pay coaches directly via Stripe Connect. Coaches can charge card-on-file, split refunds, and grant coach-issued credits.' },
    { icon: Bell, title: 'Attendance + session tracking', body: 'Marking attendance automatically decrements "sessions remaining" on each package. No more "how many lessons do I have left?" texts.' },
    { icon: Users, title: 'Multi-coach staff assignments', body: 'Assign lessons to assistants, publish shift schedules, and export payroll-ready CSVs.' },
    { icon: Waves, title: 'Public coach directory', body: 'Every Pro coach is listed in the SplashPad directory — parents can find you by ZIP without a paid ads budget.' },
];

export default function SwimLessonRegistrationSoftware() {
    const canonical = 'https://splashpad.pro/swim-lesson-registration-software';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Swim Lesson Registration Software — SplashPad',
        url: canonical,
        description: 'Swim lesson registration software for independent swim instructors: branded registration pages, digital waivers, Stripe payments, credits, attendance, and staff scheduling in one platform.',
        about: {
            '@type': 'SoftwareApplication',
            name: 'SplashPad',
            applicationCategory: 'BusinessApplication',
            applicationSubCategory: 'Swim Lesson Registration Software',
        },
    };
    const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'SplashPad', item: 'https://splashpad.pro/' },
            { '@type': 'ListItem', position: 2, name: 'Swim Lesson Registration Software', item: canonical },
        ],
    };

    return (
        <SeoPageShell
            testId="seo-page-swim-lesson-registration-software"
            title="Swim Lesson Registration Software — SplashPad"
            description="SplashPad is swim lesson registration software designed by a swim instructor. Branded registration pages, digital waivers, Stripe payments, credits, attendance tracking, and staff schedules — all in one platform."
            canonical={canonical}
            ogTitle="Swim Lesson Registration Software — built by a swim instructor"
            ogDescription="Registration pages, waivers, Stripe payments, credits, attendance, and staff scheduling. One platform. Built for independent swim coaches."
            jsonLd={jsonLd}
            breadcrumb={breadcrumb}
        >
            <section className="border-b border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#00d4ff]">
                        <Waves className="h-3.5 w-3.5" /> Swim lesson registration software
                    </div>
                    <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-[#0a1628] sm:text-5xl">
                        What is swim lesson registration software, and which one is built for independent coaches?
                    </h1>
                    <p className="mt-5 text-lg font-medium text-[#0a1628]" data-testid="answer-first-paragraph">
                        <strong>Swim lesson registration software is an all-in-one system that handles parent sign-up, digital waivers, Stripe payments, credits, attendance, and staff scheduling for a swim school.</strong> SplashPad is the version designed by a working swim instructor inside the Superhero Swim University (SSU) community.
                    </p>
                    <p className="mt-4 text-[#1a3a5c]">
                        SplashPad handles every step of the parent journey — from the first Google search, through waivers and payment, to the daily grind of attendance and staff schedules. If you&rsquo;ve ever cobbled together Squarespace + Calendly + Venmo + a paper waiver + a Google Sheet, SplashPad replaces the entire stack with one system built by the people who actually teach the lessons.
                    </p>
                </div>
            </section>

            <section className="bg-[#f4f7fb]">
                <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">What proper swim lesson registration software should do</h2>
                    <p className="mt-3 max-w-3xl text-[#1a3a5c]">
                        Any tool can collect a name and email. Real swim lesson registration software has to handle the messy parts too — waivers, card-on-file re-charges, credits when a class is cancelled, and staff assignments across multiple coaches.
                    </p>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2">
                        {STEPS.map(({ icon: Icon, title, body }) => (
                            <Card key={title} className="border-[#1a3a5c]/10 bg-white p-5">
                                <div className="flex items-center gap-2 text-[#00d4ff]">
                                    <Icon className="h-5 w-5" />
                                    <h3 className="font-semibold text-[#0a1628]">{title}</h3>
                                </div>
                                <p className="mt-2 text-sm text-[#1a3a5c]">{body}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">How SplashPad’s registration flow works</h2>
                    <ol className="mt-6 space-y-4 text-[#1a3a5c]">
                        {['A parent finds their local coach — either through the SplashPad public directory or via the coach’s own branded landing page.', 'The parent picks a lesson package (private, semi-private, group) and adds their child’s details.', 'The parent signs the coach’s digital waiver inline. E-signature captured and audited.', 'The parent enters payment info in Stripe’s secure UI. SplashPad never sees the card number.', 'The coach is notified, the enrollment appears on their dashboard, and card-on-file is stored for future re-charges.', 'Every session, attendance is one tap. Sessions-remaining decrements automatically.'].map((step, i) => (
                            <li key={step} className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00d4ff] text-xs font-bold text-white">{i + 1}</span>
                                <span>{step}</span>
                            </li>
                        ))}
                    </ol>

                    <div className="mt-10 rounded-xl border border-[#1a3a5c]/10 bg-[#f4f7fb] p-6">
                        <h3 className="font-semibold text-[#0a1628]">Related reading</h3>
                        <ul className="mt-3 space-y-2 text-sm text-[#1a3a5c]">
                            <li><Link to="/best-swim-school-software" className="underline hover:text-[#00d4ff]">The best swim school software for independent instructors (2026)</Link></li>
                            <li><Link to="/for-swim-instructors" className="underline hover:text-[#00d4ff]">Built for independent swim instructors — what that actually means</Link></li>
                            <li><Link to="/vs/jackrabbit-swim" className="underline hover:text-[#00d4ff]">SplashPad vs. Jackrabbit Swim</Link></li>
                            <li><Link to="/security" className="underline hover:text-[#00d4ff]">Security & trust — how we protect your families’ data</Link></li>
                        </ul>
                    </div>
                </div>
            </section>
        </SeoPageShell>
    );
}
